import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Search, RefreshCw, MoreHorizontal, Play, Pause, Copy, Trash2,
  Users, GitBranch, Edit, Filter, ArrowRight, Zap, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  active: 'bg-green-500',
  paused: 'bg-yellow-500',
  archived: 'bg-gray-400'
};

const JourneyList = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [journeyToDelete, setJourneyToDelete] = useState(null);

  const fetchJourneys = useCallback(async () => {
    try {
      let url = `${API_URL}/api/marketing/journeys?token=${token}&limit=50`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await fetch(url);
      const data = await res.json();

      if (data.success) {
        setJourneys(data.journeys || []);
      }
    } catch (error) {
      console.error('Error fetching journeys:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  const handleActivate = async (journeyId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}/activate?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey activated');
        fetchJourneys();
      } else {
        toast.error(data.detail || 'Failed to activate');
      }
    } catch (error) {
      toast.error('Error activating journey');
    }
  };

  const handlePause = async (journeyId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}/pause?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey paused');
        fetchJourneys();
      } else {
        toast.error(data.detail || 'Failed to pause');
      }
    } catch (error) {
      toast.error('Error pausing journey');
    }
  };

  const handleDuplicate = async (journeyId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}/duplicate?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey duplicated');
        fetchJourneys();
      } else {
        toast.error(data.detail || 'Failed to duplicate');
      }
    } catch (error) {
      toast.error('Error duplicating journey');
    }
  };

  const handleDelete = async () => {
    if (!journeyToDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyToDelete}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey deleted');
        setShowDeleteDialog(false);
        setJourneyToDelete(null);
        fetchJourneys();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting journey');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="journey-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Marketing Journeys</h1>
          <p className="text-slate-500 dark:text-slate-400">Create and manage automation workflows</p>
        </div>
        <Button onClick={() => navigate('/marketing/journeys/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Create Journey
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search journeys..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchJourneys}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Journey Grid */}
      {journeys.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Journeys Yet</h3>
            <p className="text-slate-500 mb-4">Create your first automation journey to engage contacts</p>
            <Button onClick={() => navigate('/marketing/journeys/new')}>
              Create Journey
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {journeys.map((journey) => (
            <Card key={journey.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{journey.name}</h3>
                      <Badge variant="outline" className="capitalize">
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${STATUS_COLORS[journey.status]}`}></span>
                        {journey.status}
                      </Badge>
                    </div>
                    {journey.description && (
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{journey.description}</p>
                    )}
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/marketing/journeys/${journey.id}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {journey.status === 'active' ? (
                        <DropdownMenuItem onClick={() => handlePause(journey.id)}>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => handleActivate(journey.id)}>
                          <Play className="w-4 h-4 mr-2" />
                          Activate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDuplicate(journey.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setJourneyToDelete(journey.id);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-semibold">{journey.total_contacts || 0}</p>
                    <p className="text-xs text-slate-500">Contacts</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-semibold">{journey.stats?.active || 0}</p>
                    <p className="text-xs text-slate-500">Active</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-semibold">{journey.stats?.completed || 0}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-xs text-slate-500">
                    {journey.nodes?.length || 0} nodes
                  </span>
                  <span className="text-xs text-slate-500">
                    Updated {journey.updated_at && format(new Date(journey.updated_at), 'MMM d, yyyy')}
                  </span>
                </div>

                {/* Open Button */}
                <Button 
                  variant="outline" 
                  className="w-full mt-3"
                  onClick={() => navigate(`/marketing/journeys/${journey.id}`)}
                >
                  Open Builder
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Journey</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this journey? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JourneyList;
